import { safe, unwrap } from "pb.safe";
import * as v from "valibot";

import { logger } from "./shared/logger";

export const Session = v.object({
	clientName: v.optional(v.string()),
	clientVersion: v.optional(v.string()),
	vendorName: v.optional(v.string()),
	vendorApplicationName: v.optional(v.string()),
	vendorApplicationVersion: v.optional(v.string()),
	vendorDeviceType: v.optional(v.string()),
});

export type Session = v.InferOutput<typeof Session>;

export function Session_fromUrl(url?: string): Session | undefined {
	const paramsUnknown = new URLSearchParams(
		url ? new URL(url).search : window.location.search,
	);

	const paramsResult = v.safeParse(
		Session_fromUrl_schema,
		Object.fromEntries(paramsUnknown.entries()),
	);

	if (!paramsResult.success) {
		return undefined;
	}

	const params = paramsResult.output;

	/**
	 * SDKCore >= 0.1.9 <= 0.1.14 used ?session=.
	 * - Affects SDKReact >= 0.0.16 <= 0.0.20.
	 * - Affects SDKReactNative >= 0.0.21 <= 0.0.24.
	 *
	 * `session` was a b64-encoded JSON-string from object `{ origin:
	 * "ClientName:ClientVersion", timestamp: ISO1806DateTimeString }`.
	 */
	if ("session" in params) {
		return unwrap(
			safe(() => {
				const [clientName, clientVersion] = (
					JSON.parse(atob(params.session)).origin as string
				).split(":");
				return { clientName, clientVersion };
			}),
		);
	}

	/**
	 * SDKCore >= 0.0.15 uses ?n=&v=&a=.
	 * - Affects SDKReact >= 0.0.21.
	 * - Affects SDKReactNative >= 0.0.25.
	 * - Affects SDKDotNet => 0.0.2.
	 * - Affects all new SDK Clients.
	 *
	 * `n` is the client's name.
	 * `v` is the client's version.
	 * `a` is the vendor's information as a "__"-delimited string
	 * `"Name__ApplicationName__ApplicationVersion__DeviceType"`.
	 */
	const [
		vendorName,
		vendorApplicationName,
		vendorApplicationVersion,
		vendorDeviceType,
	] = params.a.split("__");

	return {
		clientName: params.n,
		clientVersion: params.v,
		vendorName,
		vendorApplicationName,
		vendorApplicationVersion,
		vendorDeviceType,
	};
}

const Session_fromUrl_schema = v.union([
	v.object({
		n: v.string(),
		v: v.string(),
		a: v.string(),
	}),
	v.object({
		session: v.string(),
	}),
]);

export function Session_fromStorage(): Session | undefined {
	try {
		const sessionJson = window.sessionStorage.getItem("zsdk.session");
		if (!sessionJson) {
			return undefined;
		}

		const session = v.parse(Session, JSON.parse(sessionJson));
		return session;
	} catch (error) {
		if (error instanceof Error) {
			logger.error("session: load from storage failed", {
				errorName: error.name,
				errorMessage: error.message,
				errorStack: error.stack,
			});
		}
	}

	return undefined;
}

export function Session_toStorage(session: Session): void | Error {
	logger.debug("session: save to storage");
	return safe(() =>
		window.sessionStorage.setItem("zsdk.session", JSON.stringify(session)),
	);
}
